import CopyrightIcon from "@mui/icons-material/Copyright";
import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Box, Typography, Dialog, DialogContent, Grow } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useAuth } from "../../context/AuthContext";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CircularProgress from "@mui/material/CircularProgress";
import CheckIcon from "@mui/icons-material/Check";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import LaptopIcon from "@mui/icons-material/Laptop";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import TabletIcon from "@mui/icons-material/Tablet";
import DevicesIcon from "@mui/icons-material/Devices";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BusinessIcon from "@mui/icons-material/Business";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CloudQueueIcon from "@mui/icons-material/CloudQueue";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import { motion, AnimatePresence } from "framer-motion";
import { useReducedMotion } from "./ui/useReducedMotion";
import {
  ThemeScope, Button, Input, Label,
  Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter,
  Alert, Badge, Separator,
} from "./ui/shadcn";

// Design tokens (Design 14 - soft pastel mint/peach, teal accent, light)
const PAGE_GRADIENT = "linear-gradient(135deg, #dcf8ee 0%, #eefbf6 38%, #fff3e8 72%, #ffe3cf 100%)";
const BTN_GRADIENT = "linear-gradient(135deg, #0d8075 0%, #0b6b64 100%)";
const INK = "#0b2a26";
const MUTED = "#4b6b66";
const TEAL = "#0f766e";
const EASE = [0.22, 1, 0.36, 1];
const FIELD_BORDER = "#4c9d90";
const FIELD_FILL = "#f0faf7";

const THEME_VARS = {
  "--background": "#ffffff",
  "--foreground": INK,
  "--card": "#ffffff",
  "--card-foreground": INK,
  "--primary": TEAL,
  "--primary-foreground": "#ffffff",
  "--secondary": "#e0f7f0",
  "--secondary-foreground": "#0b5f58",
  "--muted": FIELD_FILL,
  "--muted-foreground": MUTED,
  "--accent": "#dff6ee",
  "--destructive": "#d6334f",
  "--destructive-foreground": "#ffffff",
  "--border": "#cfe9e2",
  "--input": FIELD_BORDER,
  "--ring": "#14b8a6",
  "--radius": "1rem",
};

const MotionButton = motion.create(Button);
const MotionCard = motion.create(Card);

const AVATARS = [
  ["#d5f5ec", "#0b5f58"],
  ["#ffe6d3", "#a8481a"],
  ["#e0ecff", "#2a4fc0"],
  ["#fde4ef", "#a62460"],
];

const CHIPS = [
  {
    key: "gst", label: "GST Ready", Icon: ReceiptLongIcon, tone: "#c9f3e6", fg: "#0b5f58", dur: 5.2, delay: 0,
    pos: { top: { xs: -46, md: 56 }, left: { xs: 2, md: -150 } },
  },
  {
    key: "cloud", label: "Cloud", Icon: CloudQueueIcon, tone: "#dbe9ff", fg: "#2a4fc0", dur: 6.1, delay: 0.6,
    pos: { top: { xs: -46, md: 168 }, right: { xs: 2, md: -128 } },
  },
  {
    key: "secure", label: "Secure", Icon: VerifiedUserOutlinedIcon, tone: "#ffe0c9", fg: "#a8481a", dur: 5.7, delay: 1.2,
    pos: { bottom: { xs: -46, md: 90 }, right: { xs: 26, md: "auto" }, left: { xs: "auto", md: -132 } },
  },
];

// Floating pill chip: outer Box positions, motion element animates transform + opacity only
function FloatChip({ chip, reduce }) {
  const { Icon } = chip;
  return (
    <Box className="ld14-chipwrap" aria-hidden="true" sx={{ position: "absolute", zIndex: 0, pointerEvents: "none", ...chip.pos }}>
      <motion.div
        initial={{ opacity: 1, scale: reduce ? 1 : 0.8 }}
        animate={reduce ? { opacity: 1, scale: 1 } : { opacity: 1, scale: 1, y: [0, -9, 0] }}
        transition={reduce
          ? { duration: 0.2 }
          : {
            opacity: { duration: 0.5, delay: 0.5 + chip.delay * 0.3 },
            scale: { duration: 0.6, delay: 0.5 + chip.delay * 0.3, ease: EASE },
            y: { duration: 0.01, repeat: 0, ease: "easeInOut", delay: chip.delay },
          }}
        style={{ willChange: "transform" }}
      >
        <Badge variant="outline" className="ld14-chip">
          <span className="ld14-chipicon" style={{ background: chip.tone, color: chip.fg }}>
            <Icon sx={{ fontSize: 16 }} />
          </span>
          {chip.label}
        </Badge>
      </motion.div>
    </Box>
  );
}

// Decorative drifting shape (transform only)
function Drift({ reduce, dy = -14, dx = 0, dur = 8, delay = 0, sx }) {
  return (
    <Box
      component={motion.div}
      animate={reduce ? undefined : { y: [0, dy, 0], x: [0, dx, 0] }}
      transition={{ duration: 0.01, repeat: 0, ease: "easeInOut", delay }}
      sx={{ position: "absolute", pointerEvents: "none", willChange: "transform", ...sx }}
    />
  );
}

function LoginDesign14({ connectionString }) {
  const path = connectionString;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { login } = useAuth();
  const reduce = useReducedMotion();

  const autoIdentifier = searchParams.get("identifier");
  const autoUsername = searchParams.get("UserName");
  const autoMobile = searchParams.get("mobile");
  const autoEmail = searchParams.get("email");
  const autoPassword = searchParams.get("password");
  const redirectPathFromQuery = searchParams.get("redirect");

  const [identifier, setIdentifier] = useState("");
  const [userPassword, setUserPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isCompanySelectionVisible, setIsCompanySelectionVisible] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [isAutoLogin, setIsAutoLogin] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  // Session limit picker
  const [sessionPickerData, setSessionPickerData] = useState(null);
  const [kickingSessionId, setKickingSessionId] = useState(null);

  // Device fingerprint (created once, persisted in localStorage)
  const getDeviceId = () => {
    let did = localStorage.getItem("_did");
    if (!did) {
      if (window.crypto && typeof window.crypto.randomUUID === "function") {
        did = window.crypto.randomUUID();
      } else {
        // Fallback for HTTP (non-localhost) — crypto.randomUUID requires HTTPS
        did = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
        });
      }
      localStorage.setItem("_did", did);
    }
    return did;
  };

  const getBrowserName = () => {
    const ua = navigator.userAgent;
    if (ua.includes("Chrome") && !ua.includes("Edg")) return `Chrome on ${getOSName()}`;
    if (ua.includes("Firefox")) return `Firefox on ${getOSName()}`;
    if (ua.includes("Safari") && !ua.includes("Chrome")) return `Safari on ${getOSName()}`;
    if (ua.includes("Edg")) return `Edge on ${getOSName()}`;
    return `Browser on ${getOSName()}`;
  };

  const getOSName = () => {
    const ua = navigator.userAgent;
    if (ua.includes("Windows")) return "Windows";
    if (ua.includes("Mac")) return "macOS";
    if (ua.includes("Linux")) return "Linux";
    return "Unknown OS";
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins  = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days  = Math.floor(diff / 86400000);
    if (mins < 1)   return "just now";
    if (mins < 60)  return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const deviceIcon = (type) => {
    if (type === "mobile")  return <PhoneAndroidIcon sx={{ fontSize: 20, color: TEAL }} />;
    if (type === "tablet")  return <TabletIcon sx={{ fontSize: 20, color: TEAL }} />;
    if (type === "desktop") return <DesktopWindowsIcon sx={{ fontSize: 20, color: TEAL }} />;
    return <LaptopIcon sx={{ fontSize: 20, color: TEAL }} />;
  };

  const handleTogglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s?.trim());

  useEffect(() => {
    const handleMessage = (ev) => {
      try {
        const data = typeof ev.data === "string" ? JSON.parse(ev.data) : ev.data;
        if (!data || !data.type) return;
        if (data.type === "BIOMETRIC_LOGIN") {
          FN_HandleLogin(data.identifier, data.password);
        }
        if (data.type === "BIOMETRIC_FAILED") {
          setErrorMessage("Biometric authentication failed or cancelled.");
        }
        if (data.type === "BIOMETRIC_NOT_AVAILABLE") {
          setErrorMessage("Biometrics not available on this device.");
        }
      } catch (err) {
        console.warn("Error parsing message from native:", err);
      }
    };
    window.addEventListener("message", handleMessage);
    document.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
      document.removeEventListener("message", handleMessage);
    };
  }, []);

  const isLikelyMobile = (s) => {
    if (!s) return false;
    const cleaned = s.replace(/[\s\-\(\)\+]/g, "");
    const digits = cleaned.replace(/\D/g, "");
    const nonDigitCount = cleaned.replace(/\d/g, "").length;
    return (digits.length >= 6 && digits.length <= 15 && nonDigitCount === 0);
  };

  const determineIdentifierType = (id) => {
    const trimmed = id?.trim() || "";
    if (isEmail(trimmed)) return "email";
    if (isLikelyMobile(trimmed)) return "mobile";
    return "UserName";
  };

  useEffect(() => {
    const ai = autoIdentifier || autoUsername || autoMobile || autoEmail;
    if (ai && autoPassword) {
      setIsAutoLogin(true);
      setIdentifier(ai);
      setUserPassword(autoPassword);
      FN_HandleLogin(ai, autoPassword);
    }
  }, [autoIdentifier, autoUsername, autoMobile, autoEmail, autoPassword]);

  const FN_HandleLogin = async (overrideIdentifier, overridePassword, kickSessionId = null) => {
    setErrorMessage("");
    setIsLoading(true);
    const id = (overrideIdentifier ?? identifier ?? "").trim();
    const pwd = overridePassword ?? userPassword;

    if (!id || !pwd) {
      setErrorMessage("Please provide identifier and password.");
      setIsLoading(false);
      return;
    }

    try {
      const identifierType = determineIdentifierType(id);

      // Build device info
      let deviceInfo = {
        deviceId:   getDeviceId(),
        deviceName: getBrowserName(),
        platform:   navigator.platform,
        deviceType: "web",
      };
      if (window.electronAPI) {
        try { deviceInfo = await window.electronAPI.getDeviceInfo(); } catch (_) {}
      }

      const payload = {
        identifier: id,
        password: pwd,
        type: identifierType,
        deviceId:     deviceInfo.deviceId,
        deviceName:   deviceInfo.deviceName,
        platform:     deviceInfo.platform,
        deviceType:   deviceInfo.deviceType,
        kickSessionId: kickSessionId,
      };
      const response = await axios.post(`${path}/api/login`, payload,{ withCredentials: true });

      // Session limit reached — show device picker
      if (response.data.sessionLimitReached) {
        setSessionPickerData(response.data);
        setIsLoading(false);
        return;
      }

      if (!response.data.success) {
        setErrorMessage(response.data.message || "Invalid credentials.");
        return;
      }

      // Store session token for all future API requests
      if (response.data.sessionToken) {
        sessionStorage.setItem("sessionToken", response.data.sessionToken);
        axios.defaults.headers.common["x-session-token"] = response.data.sessionToken;
      }

      if (!overrideIdentifier && window.ReactNativeWebView?.postMessage) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({ type: "STORE_CREDENTIALS", identifier: id, password: pwd })
        );
      }

      const user = response.data.user;
      const companyRes = await axios.get(`${path}/api/companies?companyId=${user.CompId}`);

      if (!companyRes.data.success || !companyRes.data.companies?.length) {
        navigate("/registerCompany", { replace: true, state: { pendingUser: user, password: pwd } });
        return;
      }

      const currentLoginCompanyList = companyRes.data.companies;
      const actualSelectedCompany =
        currentLoginCompanyList.find((c) => c.companyId === user.companyId || c.companyId === user.CompId) ||
        currentLoginCompanyList[0];

      const allRes = await axios.get(`${path}/api/All-companies`, { params: { userId: user.UserId } });
      const allCompanies =
        allRes.data.success && allRes.data.data?.length ? allRes.data.data : currentLoginCompanyList;
      const serverIsSuperAdmin = Boolean(allRes.data.isSuperAdmin);

      await login(user, allCompanies, actualSelectedCompany, serverIsSuperAdmin);

      setCompanies(currentLoginCompanyList);
      setSelectedCompany(actualSelectedCompany);

      if (isAutoLogin || currentLoginCompanyList.length === 1) {
        FN_SelectCompany(actualSelectedCompany);
      } else {
        setIsCompanySelectionVisible(true);
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Login failed.");
      console.error("Login error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const FN_SelectCompany = (companyOverride = null) => {
    const company = companyOverride || selectedCompany;
    if (!company) {
      setErrorMessage("Please select a company.");
      return;
    }
    localStorage.setItem("selectedCompanyId", company.CompId);
    const redirectPath = redirectPathFromQuery || "/main/home";
    navigate(redirectPath);
  };

  const handleCapsLockCheck = (e) => {
    if (e.getModifierState) {
      setCapsLockOn(e.getModifierState("CapsLock"));
    }
  };

  // ── Visual helpers (styling / motion only) ─────────────────────────────────
  // Each item owns its initial/animate (no variant chain). Input groups animate transform only.
  const rise = (i, fade = true) => {
    if (reduce) return {};
    return {
      initial: fade ? { opacity: 1, y: 14 } : { y: 14 },
      animate: fade ? { opacity: 1, y: 0 } : { y: 0 },
      transition: { duration: 0.5, delay: 0.1 + i * 0.07, ease: EASE },
    };
  };

  const handleCompanyKeyDown = (e, idx) => {
    const key = e.key;
    if (key === " " || key === "Enter") {
      e.preventDefault();
      setSelectedCompany(companies[idx]);
      return;
    }
    let next = null;
    if (key === "ArrowDown" || key === "ArrowRight") next = (idx + 1) % companies.length;
    if (key === "ArrowUp" || key === "ArrowLeft") next = (idx - 1 + companies.length) % companies.length;
    if (next === null) return;
    e.preventDefault();
    setSelectedCompany(companies[next]);
    const el = document.getElementById("ld14-company-" + next);
    if (el) el.focus();
  };

  const capsHelper = (
    <div className="ld14-helper" aria-live="polite">
      <AnimatePresence initial={false}>
        {capsLockOn && (
          <motion.span
            key="caps"
            className="ld14-caps"
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduce ? 0.1 : 0.18 }}
          >
            <WarningAmberIcon sx={{ fontSize: 14, color: "#d97706" }} />Caps Lock is ON
          </motion.span>
        )}
      </AnimatePresence>
    </div>
  );

  const errorAlert = (
    <AnimatePresence initial={false}>
      {errorMessage && (
        <motion.div
          key="err"
          initial={{ opacity: 1, y: reduce ? 0 : -8 }}
          animate={{ opacity: 1, y: 0, x: reduce ? 0 : [0, -8, 8, -5, 5, 0] }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduce ? 0.15 : 0.45 }}
        >
          <Alert variant="destructive" className="ld14-alert">
            <WarningAmberIcon sx={{ fontSize: 18, flexShrink: 0 }} />
            <span>{errorMessage}</span>
          </Alert>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const screenMotion = {
    initial: reduce ? false : { x: 28 },
    animate: { x: 0, opacity: 1 },
    exit: { opacity: 0, x: reduce ? 0 : -28 },
    transition: { duration: 0.28, ease: EASE },
  };

  return (
    <ThemeScope
      vars={THEME_VARS}
      className="login14-root ld14-page"
      style={{
        position: "relative",
        width: "100%",
        maxWidth: "100%",
        overflowX: "hidden",
        display: "flex",
        flexDirection: "column",
        background: PAGE_GRADIENT,
      }}
    >
      <style>{[
        "@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&display=swap');",
        ".login14-root, .login14-root * { font-family: 'IBM Plex Sans', -apple-system, 'Segoe UI', Roboto, sans-serif !important; }",
        ".login14-root { color-scheme: light; }",
        ".ld14-page { min-height: 100vh; }",
        "@supports (min-height: 100dvh) { .ld14-page { min-height: 100dvh; } }",

        /* Card */
        ".login14-root .ld14-card { position: relative; z-index: 1; width: 100%; min-width: 0; border: 1px solid rgba(15,118,110,0.14); border-radius: clamp(26px, 4vw, 38px); background: #fff; overflow: hidden; box-shadow: 0 36px 80px -36px rgba(15,118,110,0.45), 0 10px 30px -14px rgba(249,166,108,0.35); }",
        ".login14-root .ld14-card::before { content: ''; position: absolute; left: 0; right: 0; top: 0; height: 6px; background: linear-gradient(90deg, #5eead4 0%, #a7f3d0 45%, #fdba8c 100%); pointer-events: none; z-index: 0; }",
        ".login14-root .ld14-screen { position: relative; z-index: 1; min-width: 0; }",
        ".login14-root .ld14-head { align-items: center; text-align: center; gap: .85rem; padding: clamp(1.9rem, 5vw, 3rem) clamp(1.25rem, 5vw, 2.75rem) 1.25rem; }",
        ".login14-root .ld14-headtext { min-width: 0; display: flex; flex-direction: column; align-items: center; gap: .4rem; }",
        ".login14-root .ld14-badge { width: 58px; height: 58px; border-radius: 50%; flex-shrink: 0; display: flex; align-items: center; justify-content: center; color: " + TEAL + "; background: #d5f5ec; box-shadow: 0 0 0 7px #ecfbf6; }",
        ".login14-root .ld14-title { font-size: clamp(1.55rem, 5vw, 1.95rem); font-weight: 800; letter-spacing: -.5px; line-height: 1.15; color: var(--foreground); overflow-wrap: anywhere; }",
        ".login14-root .ld14-desc { font-size: clamp(.84rem, 2.4vw, .92rem); line-height: 1.5; color: var(--muted-foreground); overflow-wrap: anywhere; }",
        ".login14-root .ld14-card .ui-card-content { padding: 0 clamp(1.25rem, 5vw, 2.75rem) 1.5rem; }",
        ".login14-root .ld14-card .ui-card-footer { padding: 0 clamp(1.25rem, 5vw, 2.75rem) clamp(1.5rem, 4vw, 2.25rem); justify-content: center; text-align: center; font-size: .9rem; color: var(--muted-foreground); }",
        ".login14-root .ld14-count { background: #e0f7f0; color: #0b5f58; border-color: #bfe8de; }",

        /* Fields */
        ".login14-root .ld14-group { min-width: 0; }",
        ".login14-root .ld14-label { color: var(--foreground); font-weight: 700; font-size: .87rem; margin-bottom: .5rem; line-height: 1.2; }",
        ".login14-root .ld14-labelrow { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: .25rem .75rem; margin-bottom: .5rem; }",
        ".login14-root .ld14-labelrow .ld14-label { margin-bottom: 0; }",
        ".login14-root .ld14-field { position: relative; opacity: 1; min-width: 0; }",
        ".login14-root .ld14-field .ui-input { height: 3.5rem; min-height: 44px; border-radius: 18px; border: 1.5px solid " + FIELD_BORDER + "; background-color: " + FIELD_FILL + " !important; color: " + INK + " !important; -webkit-text-fill-color: " + INK + " !important; caret-color: " + INK + "; opacity: 1 !important; visibility: visible; padding: 0 3rem 0 3rem; font-size: 1rem; font-weight: 600; transition: none; box-shadow: none; }",
        ".login14-root .ld14-field .ui-input.ld14-pw { padding-right: 5.6rem; }",
        ".login14-root .ld14-field .ui-input::placeholder { color: " + MUTED + "; -webkit-text-fill-color: " + MUTED + "; opacity: 1; font-weight: 500; }",
        ".login14-root .ld14-field .ui-input:hover { border-color: " + TEAL + "; }",
        ".login14-root .ld14-field .ui-input:focus, .login14-root .ld14-field .ui-input:focus-visible { background-color: #fff !important; border-color: " + TEAL + "; box-shadow: 0 0 0 4px rgba(20,184,166,0.30); outline: none; }",
        ".login14-root .ld14-field .ui-input:-webkit-autofill, .login14-root .ld14-field .ui-input:-webkit-autofill:hover, .login14-root .ld14-field .ui-input:-webkit-autofill:active { -webkit-box-shadow: 0 0 0 100px " + FIELD_FILL + " inset !important; box-shadow: 0 0 0 100px " + FIELD_FILL + " inset !important; -webkit-text-fill-color: " + INK + " !important; caret-color: " + INK + "; border-color: " + FIELD_BORDER + "; transition: background-color 9999s ease-out 0s; }",
        ".login14-root .ld14-field .ui-input:-webkit-autofill:focus { -webkit-box-shadow: 0 0 0 100px #fff inset, 0 0 0 4px rgba(20,184,166,0.30) !important; box-shadow: 0 0 0 100px #fff inset, 0 0 0 4px rgba(20,184,166,0.30) !important; -webkit-text-fill-color: " + INK + " !important; border-color: " + TEAL + "; }",
        ".login14-root .ld14-field .ui-input:autofill { background-color: " + FIELD_FILL + " !important; color: " + INK + " !important; -webkit-text-fill-color: " + INK + " !important; }",
        ".login14-root .ld14-field input::-ms-reveal, .login14-root .ld14-field input::-ms-clear { display: none; }",
        ".login14-root .ld14-field input::-webkit-credentials-auto-fill-button, .login14-root .ld14-field input::-webkit-contacts-auto-fill-button { display: none !important; visibility: hidden; pointer-events: none; }",
        ".login14-root .ld14-fieldicon { position: absolute; left: 1rem; top: 50%; margin-top: -11px; color: #3f8c80; pointer-events: none; z-index: 1; }",
        ".login14-root .ld14-field:focus-within .ld14-fieldicon { color: " + TEAL + "; }",
        ".login14-root .ld14-capsicon { position: absolute; right: 1rem; top: 50%; margin-top: -10px; pointer-events: none; z-index: 1; }",
        ".login14-root .ld14-capsicon-pw { right: 3.6rem; }",
        ".login14-root .ld14-toggle { position: absolute; right: .4rem; top: 50%; margin-top: -1.4rem; height: 2.8rem; width: 2.8rem; min-width: 44px; min-height: 44px; color: #2f6f66; border-radius: 14px; z-index: 2; }",
        ".login14-root .ld14-toggle:hover { color: " + TEAL + "; background: #dff6ee; }",
        ".login14-root .ld14-helper { min-height: 1.35rem; padding-top: .3rem; }",
        ".login14-root .ld14-caps { display: inline-flex; align-items: center; gap: .35rem; color: #b45309; font-size: 12px; font-weight: 600; }",
        ".login14-root .ld14-link { color: " + TEAL + "; text-decoration: none; font-weight: 800; font-size: .86rem; border-radius: 4px; display: inline-flex; align-items: center; min-height: 44px; }",
        ".login14-root .ld14-link:hover { text-decoration: underline; text-underline-offset: 3px; }",
        ".login14-root .ld14-link:focus-visible { outline: 2px solid " + TEAL + "; outline-offset: 3px; }",
        ".login14-root .ld14-labelrow .ld14-link { min-height: 32px; }",
        ".login14-root .ld14-alert { display: flex; align-items: center; gap: .6rem; background: #fff1ee; border-color: #f4b4a6; color: #b3261e; font-weight: 600; font-size: .85rem; margin-bottom: 1rem; border-radius: 16px; overflow-wrap: anywhere; }",

        /* Buttons */
        ".login14-root .ld14-primary { width: 100%; height: 3.5rem; min-height: 44px; border-radius: 18px; font-size: 1rem; font-weight: 800; letter-spacing: .2px; color: #fff; background: " + BTN_GRADIENT + "; box-shadow: 0 14px 28px -12px rgba(15,118,110,0.65); transition: transform .15s, opacity .15s; }",
        ".login14-root .ld14-primary:hover { opacity: 1; }",
        ".login14-root .ld14-primary:disabled { opacity: .7; box-shadow: none; }",
        ".login14-root .ld14-primary svg { transition: transform .2s ease; }",
        ".login14-root .ld14-primary:hover svg { transform: translateX(4px); }",
        ".login14-root .ld14-bio { width: 100%; height: 3rem; min-height: 44px; margin-top: .75rem; border-radius: 18px; font-weight: 700; color: " + TEAL + "; border-color: #7cc4b8; }",
        ".login14-root .ld14-bio:hover { background: #e6f8f2; border-color: " + TEAL + "; }",

        /* Company cards */
        ".login14-root .ld14-companylist { display: flex; flex-direction: column; gap: .7rem; max-height: min(320px, 42vh); min-height: 0; overflow-y: auto; overflow-x: hidden; padding: 6px; margin: 0 -6px 1.1rem; }",
        ".login14-root .ld14-company { display: flex; align-items: center; gap: .9rem; min-width: 0; min-height: 44px; padding: .85rem 1rem; border-radius: 20px; border: 1.5px solid #b9ddd5; background: #f6fcfa; cursor: pointer; outline: none; box-shadow: none; }",
        ".login14-root .ld14-company:hover { border-color: " + FIELD_BORDER + "; }",
        ".login14-root .ld14-company:focus-visible { box-shadow: 0 0 0 2px #fff, 0 0 0 4px var(--ring); }",
        ".login14-root .ld14-company[aria-checked='true'] { border-color: " + TEAL + "; background: #e6f8f2; box-shadow: 0 0 0 4px rgba(20,184,166,0.22); }",
        ".login14-root .ld14-company[aria-checked='true']:focus-visible { box-shadow: 0 0 0 2px #fff, 0 0 0 5px var(--ring); }",
        ".login14-root .ld14-avatar { width: 42px; height: 42px; border-radius: 14px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 1rem; color: var(--av-fg); background: var(--av-bg); }",
        ".login14-root .ld14-company[aria-checked='true'] .ld14-avatar { color: #fff; background: " + BTN_GRADIENT + "; }",
        ".login14-root .ld14-companyname { flex: 1; min-width: 0; font-weight: 700; font-size: .95rem; color: var(--foreground); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; word-break: break-word; }",
        ".login14-root .ld14-radio { width: 24px; height: 24px; border-radius: 50%; flex-shrink: 0; display: flex; align-items: center; justify-content: center; border: 2px solid #9fd3c8; background: #fff; }",
        ".login14-root .ld14-company[aria-checked='true'] .ld14-radio { border-color: " + TEAL + "; background: " + TEAL + "; color: #fff; }",

        /* Hero bits */
        ".login14-root .ld14-eyebrow { background: rgba(255,255,255,0.75); border: 1px solid #bfe8de; color: " + TEAL + "; padding: .4rem .95rem; gap: .55rem; font-size: .7rem; font-weight: 800; letter-spacing: 1.5px; text-transform: uppercase; max-width: 100%; text-align: center; white-space: normal; }",
        ".login14-root .ld14-chip { gap: .5rem; padding: .4rem .95rem .4rem .4rem; background: #fff; border: 1px solid rgba(15,118,110,0.16); color: " + INK + "; font-size: .84rem; font-weight: 800; white-space: nowrap; box-shadow: 0 10px 22px -12px rgba(15,118,110,0.45); }",
        ".login14-root .ld14-chipicon { width: 26px; height: 26px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; }",

        /* Session dialog buttons */
        ".login14-root .ld14-signout { min-width: 82px; min-height: 44px; flex-shrink: 0; font-size: .78rem; font-weight: 800; border-radius: 12px; background: #d6334f; color: #fff; }",
        ".login14-root .ld14-cancel { width: 100%; min-height: 44px; font-weight: 700; color: " + MUTED + "; border-radius: 14px; }",
        ".login14-root .ld14-cancel:hover { background: #f0faf7; border-color: " + FIELD_BORDER + "; color: " + TEAL + "; }",

        /* Small phones */
        "@media (max-width: 380px) { .login14-root .ld14-eyebrow { letter-spacing: 1px; font-size: .62rem; padding: .35rem .7rem; } .login14-root .ld14-field .ui-input { font-size: .95rem; } }",
        /* Short viewports / landscape phones: tighten and drop floating chips so the form leads */
        "@media (max-height: 520px) { .login14-root .ld14-chipwrap { display: none !important; } .login14-root .ld14-head { padding-top: 1.4rem; padding-bottom: .8rem; gap: .5rem; } .login14-root .ld14-badge { width: 44px; height: 44px; box-shadow: 0 0 0 5px #ecfbf6; } .login14-root .ld14-helper { min-height: 1rem; padding-top: .15rem; } .login14-root .ld14-companylist { max-height: 180px; } }",

        "@media (prefers-reduced-motion: reduce) { .login14-root, .login14-root *, .login14-root *::before, .login14-root *::after { animation-duration: .01ms !important; animation-iteration-count: 1 !important; transition-duration: .01ms !important; } }",
      ].join("\n")}</style>

      {/* ── DECORATIVE BACKGROUND (static glows + transform-only drift) ── */}
      <Box aria-hidden="true" sx={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
        <Box sx={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(rgba(15,118,110,0.22) 1px, transparent 1px)", backgroundSize: "24px 24px", opacity: 0.35, maskImage: "linear-gradient(180deg, #000 0%, transparent 80%)", WebkitMaskImage: "linear-gradient(180deg, #000 0%, transparent 80%)" }} />
        <Box sx={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse 50% 45% at 8% 6%, rgba(94,234,212,0.30) 0%, transparent 70%), radial-gradient(ellipse 50% 45% at 96% 96%, rgba(253,186,140,0.38) 0%, transparent 70%)" }} />
        <Drift reduce={reduce} dy={-14} dur={7} sx={{ top: "8%", left: "5%", width: { xs: 56, md: 120 }, height: { xs: 56, md: 120 }, borderRadius: "50%", background: "radial-gradient(circle at 30% 30%, #ffffff 0%, #bff3e4 72%)", opacity: 0.8 }} />
        <Drift reduce={reduce} dy={-16} dx={8} dur={9} delay={1} sx={{ bottom: "8%", right: "5%", width: { xs: 80, md: 160 }, height: { xs: 80, md: 160 }, borderRadius: "50%", background: "radial-gradient(circle at 30% 30%, #ffffff 0%, #ffd2b0 75%)", opacity: 0.8 }} />
        <Drift reduce={reduce} dy={-10} dur={6} delay={0.5} sx={{ top: "16%", right: "12%", width: { xs: 32, md: 56 }, height: { xs: 32, md: 56 }, borderRadius: "50%", border: "8px solid rgba(94,234,212,0.45)" }} />
        <Drift reduce={reduce} dy={-12} dur={8} delay={2} sx={{ bottom: "20%", left: "10%", width: { xs: 22, md: 36 }, height: { xs: 22, md: 36 }, borderRadius: "50%", background: "#ffc9a3", opacity: 0.7 }} />
      </Box>

      {/* ── CONTENT ── */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          flex: 1,
          width: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          px: { xs: 2, sm: 3, md: 4 },
          pt: { xs: 3, sm: 5, md: 6 },
          pb: { xs: 2.5, sm: 3, md: 5 },
          "@media (max-height: 520px)": { pt: 2, pb: 2 },
        }}
      >
        <Box sx={{ my: "auto", width: "100%", maxWidth: { xs: 480, md: 520 }, minWidth: 0 }}>
          {/* Logo + eyebrow */}
          <Box
            component={motion.div}
            {...(reduce ? {} : { initial: { opacity: 1, y: -14 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6, ease: EASE } })}
            sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.4, textAlign: "center", mb: { xs: 7, md: 3.5 }, "@media (max-height: 520px)": { mb: 2, gap: 0.8 } }}
          >
            <Box component="img" src="/RBLOGOBLUE.svg" alt="ReonBook" sx={{ height: { xs: 38, md: 46 }, maxWidth: "70%" }} />
          </Box>

          {/* Card wrapper with floating chips around it */}
          <Box sx={{ position: "relative", width: "100%", mb: { xs: 7, md: 0 }, "@media (max-height: 520px)": { mb: 0 } }}>
            {CHIPS.map((c) => <FloatChip key={c.key} chip={c} reduce={reduce} />)}

            <MotionCard
              className="ld14-card"
              initial={{ y: reduce ? 0 : 36, scale: reduce ? 1 : 0.97 }}
              animate={{ y: 0, scale: 1 }}
              transition={{ duration: reduce ? 0.2 : 0.8, delay: reduce ? 0 : 0.08, ease: EASE }}
            >
              <AnimatePresence mode="wait">
                {!isCompanySelectionVisible ? (

                  /* ── LOGIN FORM ── */
                  <motion.div key="login" className="ld14-screen" {...screenMotion}>
                    <CardHeader className="ld14-head">
                      <motion.div {...rise(0)} className="ld14-badge" aria-hidden="true">
                        <LockOutlinedIcon sx={{ fontSize: 27 }} />
                      </motion.div>
                      <motion.div {...rise(1)} className="ld14-headtext">
                        <CardTitle role="heading" aria-level={1} className="ld14-title">Welcome</CardTitle>
                        <CardDescription className="ld14-desc">Sign in to access your ReonBook dashboard</CardDescription>
                      </motion.div>
                    </CardHeader>

                    <CardContent>
                      <form onSubmit={(e) => { e.preventDefault(); setIsAutoLogin(false); FN_HandleLogin(); }}>

                        <motion.div {...rise(2, false)} className="ld14-group">
                          <Label htmlFor="ld14-identifier" className="ld14-label">Username, Email or Mobile</Label>
                          <div className="ld14-field">
                            <PersonOutlineIcon className="ld14-fieldicon" sx={{ fontSize: 22 }} aria-hidden="true" />
                            <Input
                              id="ld14-identifier"
                              type="text"
                              autoComplete="username"
                              placeholder="Username, email or mobile number"
                              value={identifier}
                              onChange={(e) => setIdentifier(e.target.value)}
                              onKeyUp={handleCapsLockCheck} onFocus={handleCapsLockCheck} onBlur={() => setCapsLockOn(false)}
                            />
                            {capsLockOn && <WarningAmberIcon className="ld14-capsicon" sx={{ color: "#f59e0b", fontSize: 20 }} aria-hidden="true" />}
                          </div>
                          {capsHelper}
                        </motion.div>

                        <motion.div {...rise(3, false)} className="ld14-group" style={{ marginTop: ".6rem" }}>
                          <div className="ld14-labelrow">
                            <Label htmlFor="ld14-password" className="ld14-label">Password</Label>
                            <Link to="/forgot-password" className="ld14-link">Forgot password?</Link>
                          </div>
                          <div className="ld14-field">
                            <LockOutlinedIcon className="ld14-fieldicon" sx={{ fontSize: 22 }} aria-hidden="true" />
                            <Input
                              id="ld14-password"
                              className="ld14-pw"
                              type={showPassword ? "text" : "password"}
                              autoComplete="current-password"
                              placeholder="Enter your password"
                              value={userPassword}
                              onChange={(e) => setUserPassword(e.target.value)}
                              onKeyUp={handleCapsLockCheck} onFocus={handleCapsLockCheck} onBlur={() => setCapsLockOn(false)}
                            />
                            {capsLockOn && <WarningAmberIcon className="ld14-capsicon ld14-capsicon-pw" sx={{ color: "#f59e0b", fontSize: 20 }} aria-hidden="true" />}
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="ld14-toggle"
                              onClick={handleTogglePasswordVisibility}
                              aria-label={showPassword ? "Hide password" : "Show password"}
                              aria-pressed={showPassword}
                            >
                              {showPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                            </Button>
                          </div>
                          {capsHelper}
                        </motion.div>

                        <motion.div {...rise(4)} style={{ marginTop: ".6rem" }}>
                          {errorAlert}

                          <MotionButton
                            type="submit"
                            size="lg"
                            disabled={isLoading}
                            aria-busy={isLoading}
                            className="ld14-primary"
                            whileHover={reduce || isLoading ? undefined : { y: -2 }}
                            whileTap={reduce || isLoading ? undefined : { scale: 0.98 }}
                            transition={{ type: "spring", stiffness: 400, damping: 22 }}
                          >
                            {isLoading
                              ? <><CircularProgress size={18} thickness={5} sx={{ color: "#fff" }} />Signing in...</>
                              : <>Sign In<ArrowForwardIcon sx={{ fontSize: 20 }} /></>}
                          </MotionButton>

                          {typeof window !== "undefined" && window.isNativeApp && window.ReactNativeWebView && (
                            <Button
                              type="button"
                              variant="outline"
                              className="ld14-bio"
                              onClick={() => window.ReactNativeWebView?.postMessage(JSON.stringify({ type: "REQUEST_BIOMETRIC" }))}>
                              Login with Biometric
                            </Button>
                          )}
                        </motion.div>
                      </form>

                      <motion.div {...rise(5)}>
                        <Separator style={{ margin: "1.5rem 0 0" }} />
                      </motion.div>
                    </CardContent>

                    <CardFooter>
                      <motion.p {...rise(6)} style={{ margin: 0 }}>
                        Don't have an account?{" "}
                        <Link to="/register" className="ld14-link" style={{ fontSize: "inherit" }}>
                          Sign up now
                        </Link>
                      </motion.p>
                    </CardFooter>
                  </motion.div>

                ) : (

                  /* ── COMPANY SELECTION ── */
                  <motion.div key="company" className="ld14-screen" {...screenMotion}>
                    <CardHeader className="ld14-head">
                      <motion.div {...rise(0)} className="ld14-badge" aria-hidden="true">
                        <BusinessIcon sx={{ fontSize: 27 }} />
                      </motion.div>
                      <motion.div {...rise(1)} className="ld14-headtext">
                        <CardTitle role="heading" aria-level={1} className="ld14-title">Select Organization</CardTitle>
                        <CardDescription className="ld14-desc" id="ld14-company-desc">Choose which organization to access</CardDescription>
                        <Badge variant="secondary" className="ld14-count">{companies.length} organization{companies.length !== 1 ? "s" : ""}</Badge>
                      </motion.div>
                    </CardHeader>

                    <CardContent>
                      <motion.div
                        {...rise(2, false)}
                        className="ld14-companylist"
                        role="radiogroup"
                        aria-label="Choose your organization"
                        aria-describedby="ld14-company-desc"
                      >
                        {companies.map((c, idx) => {
                          const isSel = selectedCompany?.companyId === c.companyId;
                          const firstFocusable = !selectedCompany && idx === 0;
                          const av = AVATARS[idx % AVATARS.length];
                          return (
                            <MotionCard
                              key={c.companyId}
                              id={"ld14-company-" + idx}
                              role="radio"
                              aria-checked={isSel}
                              tabIndex={isSel || firstFocusable ? 0 : -1}
                              className="ld14-company"
                              onClick={() => setSelectedCompany(companies.find((x) => x.companyId === c.companyId))}
                              onKeyDown={(e) => handleCompanyKeyDown(e, idx)}
                              whileHover={reduce ? undefined : { y: -2 }}
                              whileTap={reduce ? undefined : { scale: 0.99 }}
                              transition={{ type: "spring", stiffness: 380, damping: 24 }}
                            >
                              <span className="ld14-avatar" style={{ "--av-bg": av[0], "--av-fg": av[1] }} aria-hidden="true">{(c.companyName || "?").trim().charAt(0).toUpperCase()}</span>
                              <span className="ld14-companyname" title={c.companyName}>{c.companyName}</span>
                              <span className="ld14-radio" aria-hidden="true">
                                <AnimatePresence initial={false}>
                                  {isSel && (
                                    <motion.span
                                      key="tick"
                                      initial={{ scale: 0 }}
                                      animate={{ scale: 1 }}
                                      exit={{ scale: 0 }}
                                      transition={{ duration: reduce ? 0.05 : 0.18 }}
                                      style={{ display: "flex" }}
                                    >
                                      <CheckIcon sx={{ fontSize: 15 }} />
                                    </motion.span>
                                  )}
                                </AnimatePresence>
                              </span>
                            </MotionCard>
                          );
                        })}
                      </motion.div>

                      <motion.div {...rise(3)}>
                        {errorAlert}

                        <MotionButton
                          type="button"
                          size="lg"
                          className="ld14-primary"
                          onClick={() => FN_SelectCompany()}
                          whileHover={reduce ? undefined : { y: -2 }}
                          whileTap={reduce ? undefined : { scale: 0.98 }}
                          transition={{ type: "spring", stiffness: 400, damping: 22 }}
                        >
                          Continue to Dashboard<ArrowForwardIcon sx={{ fontSize: 20 }} />
                        </MotionButton>
                      </motion.div>
                    </CardContent>
                  </motion.div>
                )}
              </AnimatePresence>
            </MotionCard>
          </Box>

          {/* Footer */}
          <Typography
            component={motion.p}
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            transition={{ delay: reduce ? 0 : 0.9, duration: 0.6 }}
            align="center"
            sx={{ fontSize: "0.75rem", color: MUTED, m: 0, mt: { xs: 0, md: 4 }, "@media (max-height: 520px)": { mt: 2 } }}
          >
            <CopyrightIcon sx={{ fontSize: 12, verticalAlign: "middle", mr: 0.4 }} />
            {new Date().getFullYear()} Powered by{" "}
            Soltrack Technologies Pvt Ltd
          </Typography>
        </Box>
      </Box>

      {/* ── SESSION LIMIT PICKER MODAL ── */}
      <Dialog
        className="login14-root"
        open={!!sessionPickerData}
        onClose={() => setSessionPickerData(null)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="ld14-session-title"
        slots={{ transition: Grow }}
        transitionDuration={{ enter: 320, exit: 180 }}
        slotProps={{
          backdrop: { sx: { bgcolor: "rgba(11,42,38,0.45)" } },
          paper: { sx: { borderRadius: "28px", overflow: "hidden", m: 2, width: "calc(100% - 32px)", maxHeight: "calc(100% - 32px)" } },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <ThemeScope vars={THEME_VARS} className="login14-root">
            {/* Top banner */}
            <Box sx={{ position: "relative", overflow: "hidden", background: "linear-gradient(135deg, #0a4f49 0%, #0f766e 100%)", px: { xs: 2, sm: 3 }, pt: 3, pb: 2.5 }}>
              <Box aria-hidden="true" sx={{ position: "absolute", pointerEvents: "none", width: 170, height: 170, borderRadius: "50%", top: -70, right: -40, background: "linear-gradient(135deg, rgba(255,255,255,0.22), rgba(255,255,255,0.02))" }} />
              <Box aria-hidden="true" sx={{ position: "absolute", pointerEvents: "none", width: 90, height: 90, borderRadius: "50%", bottom: -36, left: "42%", background: "linear-gradient(135deg, rgba(253,186,140,0.55), rgba(253,186,140,0.05))" }} />
              <Box sx={{ position: "relative", display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                <Box sx={{ width: 42, height: 42, borderRadius: "14px", bgcolor: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.3)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <DevicesIcon sx={{ color: "#fff", fontSize: 22 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography id="ld14-session-title" component="h2" sx={{ fontWeight: 800, fontSize: "1.08rem", color: "#fff", lineHeight: 1.3 }}>
                    Session Limit Reached
                  </Typography>
                  <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.88)", mt: 0.2 }}>
                    {sessionPickerData?.maxSessions} of {sessionPickerData?.maxSessions} allowed session{sessionPickerData?.maxSessions !== 1 ? "s" : ""} in use
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ position: "relative", bgcolor: "rgba(255,255,255,0.14)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "14px", px: 2, py: 1 }}>
                <Typography sx={{ fontSize: "0.8rem", color: "#fff", lineHeight: 1.5 }}>
                  Sign out of one of the devices below to log in on this device.
                </Typography>
              </Box>
            </Box>

            {/* Device list */}
            <Box sx={{ px: { xs: 2, sm: 2.5 }, pt: 2, pb: 1 }}>
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 800, color: MUTED, letterSpacing: "0.8px", textTransform: "uppercase", mb: 1.5 }}>
                Active Sessions
              </Typography>

              {sessionPickerData?.activeSessions?.map((session, idx) => (
                <Box key={session.id}
                  component={motion.div}
                  initial={{ opacity: 1, y: reduce ? 0 : 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, delay: reduce ? 0 : 0.1 + idx * 0.07, ease: EASE }}
                  whileHover={reduce ? undefined : { y: -1 }}
                  sx={{
                    display: "flex", alignItems: "center", gap: 1.5, minWidth: 0,
                    p: 1.5, mb: 1, borderRadius: "16px",
                    border: "1px solid #cfe9e2",
                    bgcolor: FIELD_FILL,
                    "&:hover": { borderColor: FIELD_BORDER },
                  }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: "13px", bgcolor: "#d5f5ec", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {deviceIcon(session.device_type)}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography noWrap sx={{ fontWeight: 700, fontSize: "0.87rem", color: INK }}>
                      {session.device_name || "Unknown Device"}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.3, flexWrap: "wrap" }}>
                      <AccessTimeIcon sx={{ fontSize: 12, color: MUTED }} />
                      <Typography sx={{ fontSize: "0.73rem", color: MUTED }}>
                        {timeAgo(session.last_activity)}
                      </Typography>
                      {session.ip_address && (
                        <Typography sx={{ fontSize: "0.73rem", color: MUTED, ml: 0.5, overflowWrap: "anywhere" }}>
                          · {session.ip_address}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    className="ld14-signout"
                    disabled={kickingSessionId === session.id}
                    onClick={async () => {
                      setKickingSessionId(session.id);
                      setSessionPickerData(null);
                      await FN_HandleLogin(identifier, userPassword, session.id);
                      setKickingSessionId(null);
                    }}
                  >
                    {kickingSessionId === session.id
                      ? <CircularProgress size={13} sx={{ color: "#fff" }} />
                      : "Sign Out"}
                  </Button>
                </Box>
              ))}
            </Box>

            {/* Cancel */}
            <Box sx={{ px: { xs: 2, sm: 2.5 }, pb: 2.5, pt: 0.5, textAlign: "center" }}>
              <Button
                type="button"
                variant="outline"
                className="ld14-cancel"
                onClick={() => setSessionPickerData(null)}
              >
                Cancel
              </Button>
            </Box>
          </ThemeScope>
        </DialogContent>
      </Dialog>

    </ThemeScope>
  );
}

export default LoginDesign14;
